// Live-update seam over @capgo/capacitor-updater: download a newer SPA bundle, list what is on the phone, switch to
// one. apps/core decides WHEN (apps/core/src/app/liveUpdate.ts); this file only talks to the plugin, so apps/core
// never imports it directly. Every call is a no-op where the plugin is absent — on web, and in every app binary
// from before the plugin shipped (App Store 1.1.0 and older), which therefore never live-update.
import { Capacitor } from "@capacitor/core";
import { CapacitorUpdater } from "@capgo/capacitor-updater";

/** A bundle as the manifest describes it. `checksum` and `sessionKey` are signed; the plugin verifies both. */
export interface LiveBundle {
  version: string;
  url: string;
  checksum: string;
  sessionKey: string;
}

/** True when this binary carries the updater plugin. */
export function liveUpdateSupported(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("CapacitorUpdater");
}

/**
 * Tell the plugin this bundle started. MUST run early on every launch: a bundle that has not called it within the
 * configured window (capacitor.config.ts, 10 s) is treated as broken and the app rolls back to the previous one.
 */
export async function markBundleHealthy(): Promise<void> {
  if (!liveUpdateSupported()) return;
  try {
    await CapacitorUpdater.notifyAppReady();
  } catch {
    // Never fails per the plugin's contract; a throw here must not take the app down.
  }
}

/** A bundle already on the phone, as the plugin lists it. */
export interface DownloadedBundle {
  id: string;
  version: string;
  status: string;
}

/** Every downloaded bundle on the phone (not the built-in one). Empty where live updates are unsupported. */
export async function downloadedBundles(): Promise<DownloadedBundle[]> {
  if (!liveUpdateSupported()) return [];
  const { bundles } = await CapacitorUpdater.list();
  return bundles.map(({ id, version, status }) => ({ id, version, status }));
}

/** An intact download — one the plugin has not marked failed or removed. */
export function isIntact(bundle: DownloadedBundle): boolean {
  return bundle.status === "success" || bundle.status === "pending";
}

/**
 * Download `bundle` unless an intact copy is already on the phone. Nothing changes on screen: the app switches to
 * it at the start of a later cold launch (`applyBundle`). The plugin's own staging (`next`) is NOT used — it installs
 * only when the app goes to the background, reloading it while the reader is away and dropping whatever was open.
 * Resolves the bundle's id.
 */
export async function downloadBundle(bundle: LiveBundle): Promise<string> {
  const existing = (await downloadedBundles()).find((b) => b.version === bundle.version && isIntact(b));
  if (existing) return existing.id;
  const info = await CapacitorUpdater.download({
    url: bundle.url,
    version: bundle.version,
    checksum: bundle.checksum,
    sessionKey: bundle.sessionKey,
  });
  return info.id;
}

/**
 * Switch to a downloaded bundle NOW: the app reloads into it and this promise never resolves. If that bundle then
 * fails to call notifyAppReady in time, the plugin rolls back and marks it failed.
 */
export async function applyBundle(id: string): Promise<void> {
  if (!liveUpdateSupported()) throw new Error("live update unsupported");
  await CapacitorUpdater.set({ id });
}
